import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useIsMobile from '../../hooks/useIsMobile';
import type { FilterGroup } from '../../components/mobile';
import { Layout, Table, Button, Select, Tag, message, Spin, Card, Form, Input, Switch, Popconfirm } from 'antd';
import {
  ReloadOutlined,
  SearchOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import { ServiceType } from '../../services/api';
import { ExecutionResponseDto, PublishedStrategy } from '../../types';
import type { DailyScheduleResponseDto, CreateDailyScheduleDto, UpdateDailyScheduleDto } from '../../api-schema/generated/types';
import {
  HomeContainer,
  PageHeader,
  PageTitle,
  PageSubtitle,
  SearchFilterBar,
  SearchInput,
  FilterSelect,
  EmptyState,
  LoadingContainer
} from '../Home/styles';
import styled from 'styled-components';
import MobileExecutions from './MobileExecutions';
import { executionStatusMeta, matchesStatusFilter, STATUS_FILTER_OPTIONS } from './executionStatus';

/** API may return portfolioId; generated type has portfolioName only */
type DailySchedule = DailyScheduleResponseDto & { portfolioId?: string };

const { Content } = Layout;
const { Option } = Select;
const coreService = apiService.getService(ServiceType.CORE);

const ExecutionsTableWrapper = styled.div`
  background: ${({ theme }) => theme.colors.background.secondary};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 8px;
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.lg};

  .ant-table {
    background: transparent;
    color: ${({ theme }) => theme.colors.text.primary};
  }

  .ant-table-tbody > tr > td {
    color: ${({ theme }) => theme.colors.text.primary} !important;
  }

  /* A live bot is marked at the edge only; tinting the whole row drowned the status tag. */
  .ant-table-tbody > tr.active-execution > td:first-child {
    box-shadow: inset 2px 0 0 ${({ theme }) => theme.colors.trading.buy};
  }

  .ant-table-pagination {
    margin: ${({ theme }) => theme.spacing.lg} 0 0 0;
  }

  .ant-pagination-item {
    background: ${({ theme }) => theme.colors.background.tertiary} !important;
    border-color: ${({ theme }) => theme.colors.border.primary} !important;
    
    a {
      color: ${({ theme }) => theme.colors.text.primary} !important;
    }

    &.ant-pagination-item-active {
      border-color: ${({ theme }) => theme.colors.trading.buy} !important;
      
      a {
        color: ${({ theme }) => theme.colors.trading.buy} !important;
      }
    }
  }

  .ant-pagination-prev,
  .ant-pagination-next {
    .ant-pagination-item-link {
      background: ${({ theme }) => theme.colors.background.tertiary} !important;
      border-color: ${({ theme }) => theme.colors.border.primary} !important;
      color: ${({ theme }) => theme.colors.text.primary} !important;
    }
  }
`;

interface Portfolio {
  id: string;
  portfolioId: string;
  portfolioName?: string;
  name?: string;
  createdDate: string;
  status: 'active' | 'inactive' | 'closed';
}

const ExecutionsManagement: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const isMobile = useIsMobile();
  const [executions, setExecutions] = useState<ExecutionResponseDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [strategies, setStrategies] = useState<PublishedStrategy[]>([]);
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStrategy, setFilterStrategy] = useState<string>('all');
  const [filterPortfolio, setFilterPortfolio] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  // Daily run schedule
  const [dailySchedules, setDailySchedules] = useState<DailySchedule[]>([]);
  const [loadingDaily, setLoadingDaily] = useState(false);
  const [dailyForm] = Form.useForm<{ portfolioId: string; strategyId: string; cronDaily: string; isActive: boolean }>();
  const [editingDailyId, setEditingDailyId] = useState<string | null>(null);
  const [editingCron, setEditingCron] = useState('');
  const [showAddDaily, setShowAddDaily] = useState(false);
  const [savingDaily, setSavingDaily] = useState(false);

  const fetchDailySchedules = useCallback(async () => {
    try {
      setLoadingDaily(true);
      const response = await coreService.get<DailyScheduleResponseDto[]>('/daily');
      setDailySchedules((Array.isArray(response.data) ? response.data : []) as DailySchedule[]);
    } catch (err: any) {
      if (err.response?.status === 401) return;
      console.error('Error fetching daily schedules:', err);
      message.error('Failed to load daily schedules');
      setDailySchedules([]);
    } finally {
      setLoadingDaily(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) fetchDailySchedules();
  }, [isAuthenticated, fetchDailySchedules]);

  const handleCreateDaily = async (values: CreateDailyScheduleDto) => {
    try {
      setSavingDaily(true);
      await coreService.post('/daily', values);
      message.success('Daily run created');
      setShowAddDaily(false);
      dailyForm.resetFields();
      fetchDailySchedules();
      return true;
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to create daily run');
      return false;
    } finally {
      setSavingDaily(false);
    }
  };

  const handleUpdateDaily = async (id: string, values: UpdateDailyScheduleDto) => {
    try {
      setSavingDaily(true);
      await coreService.patch(`/daily/${id}`, values);
      message.success('Daily run updated');
      setEditingDailyId(null);
      fetchDailySchedules();
      return true;
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to update daily run');
      return false;
    } finally {
      setSavingDaily(false);
    }
  };

  const handleDeleteDaily = async (id: string) => {
    try {
      await coreService.delete(`/daily/${id}`);
      message.success('Daily run removed');
      fetchDailySchedules();
      return true;
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to delete daily run');
      return false;
    }
  };

  const getPortfolioDisplayName = (p: Portfolio) => p.portfolioName || p.name || p.portfolioId;
  const getPortfolioName = (portfolioId: string) => {
    const p = portfolios.find(port => port.portfolioId === portfolioId);
    return p ? getPortfolioDisplayName(p) : portfolioId;
  };
  /** Executions carry the internal portfolio id; schedules and filters use the broker id. */
  const portfolioLabelOf = (id: string | null | undefined) => {
    const p = portfolios.find((port) => port.id === id || port.portfolioId === id);
    return p ? getPortfolioDisplayName(p) : null;
  };
  const getDailySchedulePortfolioLabel = (d: DailySchedule) =>
    d.portfolioName ?? ((d.portfolioId ? getPortfolioName(d.portfolioId) : '') || '—');

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/signin');
    }
  }, [isAuthenticated, navigate]);

  // Fetch executions
  const fetchExecutions = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      if (!token) {
        navigate('/signin');
        return;
      }

      const response = await coreService.get<ExecutionResponseDto[]>('bot/executions', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (response.data && Array.isArray(response.data)) {
        setExecutions(response.data);
      }
    } catch (error: any) {
      console.error('Error fetching executions:', error);
      if (error.response?.status !== 404) {
        message.error('Failed to load executions');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Fetch strategies for filter and create
  useEffect(() => {
    const fetchStrategies = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) return;

        const response = await coreService.get('/strategies/published', {
          params: { limit: 100 },
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });

        if (response.data.data) {
          setStrategies(response.data.data);
        } else if (Array.isArray(response.data)) {
          setStrategies(response.data);
        }
      } catch (error) {
        console.error('Error fetching strategies:', error);
      }
    };

    if (isAuthenticated) {
      fetchStrategies();
    }
  }, [isAuthenticated]);

  // Fetch portfolios for filter and create
  useEffect(() => {
    const fetchPortfolios = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) return;

        const response = await coreService.get('/portfolios', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (Array.isArray(response.data)) {
          setPortfolios(response.data.map((item: any) => ({
            ...item,
            portfolioName: item.portfolioName ?? item.name ?? item.portfolioId,
          })));
        }
      } catch (error) {
        console.error('Error fetching portfolios:', error);
      }
    };

    if (isAuthenticated) {
      fetchPortfolios();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchExecutions();
    }
  }, [isAuthenticated, fetchExecutions]);

  // Filter executions based on search query and filters
  const filteredExecutions = useMemo(() => {
    let filtered = executions;

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(exec => 
        exec.id.toLowerCase().includes(query) ||
        exec.portfolioName?.toLowerCase().includes(query) ||
        exec.status.toLowerCase().includes(query) ||
        exec.strategyId.toLowerCase().includes(query)
      );
    }

    // Filter by strategy
    if (filterStrategy !== 'all') {
      filtered = filtered.filter(exec => exec.strategyId === filterStrategy);
    }

    // Filter by portfolio
    if (filterPortfolio !== 'all') {
      filtered = filtered.filter(exec => exec.portfolioId === filterPortfolio);
    }

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(exec => matchesStatusFilter(exec.status, filterStatus));
    }

    return filtered;
  }, [executions, searchQuery, filterStrategy, filterPortfolio, filterStatus]);

  const isActiveExecution = (status: string) => executionStatusMeta(status).active;

  const getStrategyName = (strategyId: string) => {
    const strategy = strategies.find(s => s.id === strategyId);
    return strategy?.name || strategyId;
  };

  const getStrategyType = (strategyId: string) => {
    const strategy = strategies.find(s => s.id === strategyId);
    return strategy?.type || null;
  };

  const getStrategyRoute = (type: string | null): string => {
    if (!type) return '/home';
    const routeMap: { [key: string]: string } = {
      'MARKET_MAKER': '/market-maker',
      'SMART_BETA': '/smart-beta',
      'PAIR_TRADING': '/pair-trading',
      'GRID': '/grid-trading',
      'GRID_TRADING': '/grid-trading',
    };
    return routeMap[type] || '/home';
  };

  const handleExecutionClick = (execution: ExecutionResponseDto) => {
    const strategyType = getStrategyType(execution.strategyId);
    const route = getStrategyRoute(strategyType);
    
    // Find the portfolio object
    const portfolio = portfolios.find(p => p.portfolioId === execution.portfolioId);
    const strategy = strategies.find(s => s.id === execution.strategyId);
    
    // Navigate with execution data
    navigate(route, {
      state: {
        executionId: execution.id,
        strategyId: execution.strategyId,
        portfolioId: portfolio?.id || null,
        execution: execution,
        strategy: strategy,
        portfolio: portfolio
      }
    });
  };

  const columns = [
    {
      title: 'Execution ID',
      dataIndex: 'id',
      key: 'id',
      width: 200,
      render: (text: string) => (
        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
          {text.substring(0, 8)}...
        </span>
      ),
    },
    {
      title: 'Strategy',
      key: 'strategy',
      width: 200,
      render: (_: any, record: ExecutionResponseDto) => getStrategyName(record.strategyId),
    },
    {
      title: 'Portfolio',
      dataIndex: 'portfolioName',
      key: 'portfolioName',
      width: 150,
      // The server leaves portfolioName null for some rows; the user's own portfolio list has it.
      render: (text: string | null, record: ExecutionResponseDto) =>
        text || portfolioLabelOf(record.portfolioId) || 'N/A',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Tag color={executionStatusMeta(status).tag}>{executionStatusMeta(status).label}</Tag>
      ),
    },
    {
      title: 'Execution Date',
      dataIndex: 'executionDate',
      key: 'executionDate',
      width: 180,
      // A trading day stored as UTC midnight: a time of day here would only be the offset.
      // numberFormat-exempt: date
      render: (date: string) => new Date(date).toLocaleDateString('en-US', { timeZone: 'UTC' }),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      // numberFormat-exempt: date
      render: (date: string) => new Date(date).toLocaleString(),
    },
  ];

  if (!isAuthenticated) {
    return null;
  }

  const mobileFilterGroups: FilterGroup[] = [
    {
      key: 'strategy',
      label: 'Strategy',
      value: filterStrategy,
      onChange: setFilterStrategy,
      options: [
        { value: 'all', label: 'All Strategies' },
        ...strategies.map((st) => ({ value: st.id, label: st.name })),
      ],
    },
    {
      key: 'portfolio',
      label: 'Portfolio',
      value: filterPortfolio,
      onChange: setFilterPortfolio,
      options: [
        { value: 'all', label: 'All Portfolios' },
        ...portfolios.map((pf: any) => ({ value: pf.portfolioId, label: pf.portfolioName })),
      ],
    },
    {
      key: 'status',
      label: 'Status',
      value: filterStatus,
      onChange: setFilterStatus,
      options: STATUS_FILTER_OPTIONS,
    },
  ];

  if (isMobile) {
    return (
      <HomeContainer>
        <MobileExecutions
          executions={filteredExecutions}
          loading={loading}
          onRefresh={fetchExecutions}
          onOpenExecution={handleExecutionClick}
          strategyName={getStrategyName}
          portfolioLabel={portfolioLabelOf}
          search={searchQuery}
          onSearch={setSearchQuery}
          filterGroups={mobileFilterGroups}
          schedules={dailySchedules}
          loadingSchedules={loadingDaily}
          scheduleLabel={getDailySchedulePortfolioLabel}
          portfolioOptions={portfolios.map((p) => ({ value: p.portfolioId, label: getPortfolioDisplayName(p) }))}
          strategyOptions={strategies.map((st) => ({ value: st.id, label: st.name }))}
          onCreateSchedule={handleCreateDaily}
          onUpdateSchedule={handleUpdateDaily}
          onDeleteSchedule={handleDeleteDaily}
        />
      </HomeContainer>
    );
  }

  return (
    <HomeContainer>
      <div>
          <style>{`
            .daily-run-popconfirm-buttons .ant-btn { min-width: 72px !important; height: 32px !important; }
            .daily-run-popconfirm-buttons .ant-btn-default { background: #2B3139 !important; border-color: #3d4148 !important; color: #e0e0e0 !important; }
            .daily-run-popconfirm-buttons .ant-btn-default:hover { background: #3d4148 !important; color: #fff !important; border-color: #3d4148 !important; }
            .daily-run-popconfirm-buttons .ant-btn-dangerous,
            .daily-run-popconfirm-buttons .ant-btn-danger { background: #F6465D !important; border-color: #F6465D !important; color: #fff !important; }
            .daily-run-popconfirm-buttons .ant-btn-dangerous:hover,
            .daily-run-popconfirm-buttons .ant-btn-danger:hover { background: #ff7875 !important; border-color: #ff7875 !important; color: #fff !important; }
          `}</style>
          <Content style={{ padding: '24px' }}>
            <PageHeader>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <PageTitle>Execution Management</PageTitle>
                  <PageSubtitle>
                    Manage and monitor your strategy executions
                  </PageSubtitle>
                </div>
              </div>
            </PageHeader>

            <Card
              className="daily-run-card"
              title={
                <span>
                  <ClockCircleOutlined style={{ marginRight: 8, color: '#0ECB81' }} />
                  Setup daily run
                </span>
              }
              style={{
                marginBottom: 24,
                background: 'rgba(30, 35, 41, 0.6)',
                border: '1px solid #2B3139',
                borderRadius: 8,
              }}
              styles={{ body: { padding: 16 } }}
            >
              <style>{`
                .daily-run-card .daily-run-switch.ant-switch { background: #3d4148 !important; }
                .daily-run-card .daily-run-switch.ant-switch-checked { background: #0ECB81 !important; }
                .daily-run-card .daily-run-switch.ant-switch-checked:hover:not(.ant-switch-disabled) { background: #0bb369 !important; }
                .daily-run-card .daily-run-save-btn { min-width: 72px; height: 32px; background: #0ECB81 !important; border-color: #0ECB81 !important; color: #fff !important; }
                .daily-run-card .daily-run-save-btn:hover { background: #0bb369 !important; border-color: #0bb369 !important; color: #fff !important; }
                .daily-run-card .daily-run-cancel-btn { min-width: 72px; height: 32px; }
                .daily-run-card .daily-run-cancel-btn:hover { background: #3d4148 !important; color: #e0e0e0 !important; border-color: #3d4148 !important; }
              `}</style>
              <p style={{ color: '#848E9C', fontSize: 13, marginBottom: 16 }}>
                Schedule strategy runs daily by portfolio. Use cron expression for time (e.g. 0 9 * * * for 9:00 AM).
              </p>

              {showAddDaily && (
                <Form
                  form={dailyForm}
                  layout="vertical"
                  onFinish={(v) => handleCreateDaily({ ...v, isActive: v.isActive !== false })}
                  style={{ marginBottom: 16, padding: 12, background: '#1E2329', borderRadius: 6 }}
                >
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))', gap: 12 }}>
                    <Form.Item name="portfolioId" label="Portfolio" rules={[{ required: true }]}>
                      <Select placeholder="Select portfolio" size="small" showSearch optionFilterProp="children">
                        {portfolios.map(p => (
                          <Option key={p.id} value={p.portfolioId}>{getPortfolioDisplayName(p)}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item name="strategyId" label="Strategy" rules={[{ required: true }]}>
                      <Select placeholder="Select strategy" size="small" showSearch optionFilterProp="children">
                        {strategies.map(s => (
                          <Option key={s.id} value={s.id}>{s.name}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item name="cronDaily" label="Cron (daily time)" rules={[{ required: true }]} initialValue="0 9 * * *">
                      <Input placeholder="0 9 * * *" size="small" />
                    </Form.Item>
                    <Form.Item name="isActive" label="Active" valuePropName="checked" initialValue={true}>
                      <Switch className="daily-run-switch" />
                    </Form.Item>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <Button type="primary" htmlType="submit" loading={savingDaily} size="middle" className="daily-run-save-btn">
                      Save
                    </Button>
                    <Button size="middle" className="daily-run-cancel-btn" onClick={() => { setShowAddDaily(false); dailyForm.resetFields(); }}>
                      Cancel
                    </Button>
                  </div>
                </Form>
              )}

              {!showAddDaily && (
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => setShowAddDaily(true)}
                  size="small"
                  style={{ marginBottom: 12, borderColor: '#0ECB81', color: '#0ECB81' }}
                >
                  Add daily run
                </Button>
              )}

              {loadingDaily ? (
                <div style={{ textAlign: 'center', padding: 16 }}><Spin size="small" /></div>
              ) : dailySchedules.length === 0 ? (
                <div style={{ color: '#848E9C', fontSize: 13 }}>No daily runs configured.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {dailySchedules.map((d) => (
                    <div
                      key={d.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 8,
                        padding: '10px 12px',
                        background: '#1E2329',
                        borderRadius: 6,
                        border: '1px solid #2B3139',
                      }}
                    >
                      {editingDailyId === d.id ? (
                        <>
                          <Input
                            size="small"
                            value={editingCron}
                            onChange={(e) => setEditingCron(e.target.value)}
                            placeholder="0 9 * * *"
                            style={{ width: 140 }}
                          />
                          <Switch
                            className="daily-run-switch"
                            checked={d.isActive}
                            onChange={(checked) => handleUpdateDaily(d.id, { isActive: checked })}
                          />
                          <div style={{ display: 'flex', gap: 4 }}>
                            <Button
                              size="middle"
                              type="primary"
                              className="daily-run-save-btn"
                              onClick={() => {
                                if (editingCron.trim()) {
                                  handleUpdateDaily(d.id, { cronDaily: editingCron.trim() });
                                  setEditingDailyId(null);
                                  setEditingCron('');
                                }
                              }}
                            >
                              Save
                            </Button>
                            <Button size="middle" className="daily-run-cancel-btn" onClick={() => { setEditingDailyId(null); setEditingCron(''); }}>Cancel</Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <span style={{ color: '#E0E0E0', fontWeight: 500 }}>{getDailySchedulePortfolioLabel(d)}</span>
                          <span style={{ color: '#848E9C' }}>{getStrategyName(d.strategyId)}</span>
                          <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#848E9C' }}>{d.cronDaily}</span>
                          <Tag color={d.isActive ? 'green' : 'default'}>{d.isActive ? 'Active' : 'Inactive'}</Tag>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <Button type="text" size="small" icon={<EditOutlined />} style={{ color: '#0ECB81' }} onClick={() => { setEditingDailyId(d.id); setEditingCron(d.cronDaily); }} />
                            <Popconfirm
                              title="Remove this daily run?"
                              onConfirm={() => handleDeleteDaily(d.id)}
                              okText="Yes"
                              cancelText="Cancel"
                              okButtonProps={{ danger: true, size: 'middle', style: { minWidth: 72, height: 32 } }}
                              cancelButtonProps={{ size: 'middle', style: { minWidth: 72, height: 32 } }}
                              overlayClassName="daily-run-popconfirm-buttons"
                            >
                              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                            </Popconfirm>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <SearchFilterBar>
              <SearchInput
                placeholder="Search executions..."
                prefix={<SearchOutlined />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                allowClear
              />
              <FilterSelect
                placeholder="Filter by Strategy"
                value={filterStrategy}
                onChange={(value) => setFilterStrategy(value as string)}
                style={{ width: 200 }}
                allowClear
              >
                <Option value="all">All Strategies</Option>
                {strategies.map(strategy => (
                  <Option key={strategy.id} value={strategy.id}>
                    {strategy.name}
                  </Option>
                ))}
              </FilterSelect>
              <FilterSelect
                placeholder="Filter by Portfolio"
                value={filterPortfolio}
                onChange={(value) => setFilterPortfolio(value as string)}
                style={{ width: 200 }}
                allowClear
              >
                <Option value="all">All Portfolios</Option>
                {portfolios.map(portfolio => (
                  <Option key={portfolio.id} value={portfolio.portfolioId}>
                    {portfolio.portfolioName}
                  </Option>
                ))}
              </FilterSelect>
              <FilterSelect
                placeholder="Filter by Status"
                value={filterStatus}
                onChange={(value) => setFilterStatus(value as string)}
                style={{ width: 180 }}
                allowClear
              >
                {STATUS_FILTER_OPTIONS.map((o) => (
                  <Option key={o.value} value={o.value}>{o.label}</Option>
                ))}
              </FilterSelect>
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchExecutions}
                style={{ borderColor: '#0ECB81', color: '#0ECB81' }}
              >
                Refresh
              </Button>
            </SearchFilterBar>

            <ExecutionsTableWrapper>
              {loading ? (
                <LoadingContainer style={{ minHeight: '120px' }}>
                  <Spin size="large" />
                </LoadingContainer>
              ) : filteredExecutions.length === 0 ? (
                <EmptyState>
                  <FileTextOutlined style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
                  <div>No executions found</div>
                </EmptyState>
              ) : (
                <Table
                  columns={columns}
                  dataSource={filteredExecutions}
                  rowKey="id"
                  rowClassName={(record) => isActiveExecution(record.status) ? 'active-execution' : ''}
                  onRow={(record) => ({
                    onClick: () => handleExecutionClick(record),
                    style: { cursor: 'pointer' }
                  })}
                  pagination={{
                    defaultPageSize: 10,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    showTotal: (total) => `Total ${total} executions`,
                  }}
                />
              )}
            </ExecutionsTableWrapper>
          </Content>
      </div>
    </HomeContainer>
  );
};

export default ExecutionsManagement;

